import { shouldShowRegistrationSuccessBanner } from './registration-success';
import {
  type RegistrationCorrectionStep,
  resolveBusinessDocumentsSectionStep,
  resolveBusinessSectionStep,
  resolveOutletSectionStep,
} from './resolve-section-step-from-progress';
import { type RegistrationProgressResponse } from './status-display';
import {
  type RegistrationVerificationState,
  getBusinessDocumentsCorrectionMessage,
  getBusinessFieldsCorrectionMessage,
  getBusinessSectionDescription,
  getOutletCorrectionMessage,
  getOutletSectionDescription,
  mapBusinessSectionToVerificationState,
  mapOutletSectionToVerificationState,
} from './verification-status';

export interface StatusVerificationSection {
  id: string;
  kind: 'business' | 'documents' | 'outlet';
  eyebrow?: string;
  title: string;
  state: RegistrationVerificationState;
  description: string;
  resumeStep: RegistrationCorrectionStep | null;
  outletId?: string;
  outletIndex?: number;
}

function mapBusinessFieldsVerificationState(
  registrationStatus: RegistrationProgressResponse['registration_status'],
  businessSection: RegistrationProgressResponse['sections']['business'],
  stateOptions: { treatCompleteAsApproved: boolean }
): RegistrationVerificationState {
  return mapBusinessSectionToVerificationState(
    registrationStatus,
    {
      ...businessSection,
      missing_documents: [],
      complete:
        businessSection.missing_fields.length === 0 &&
        businessSection.documents_complete,
    },
    stateOptions
  );
}

function mapBusinessDocumentsVerificationState(
  registrationStatus: RegistrationProgressResponse['registration_status'],
  businessSection: RegistrationProgressResponse['sections']['business'],
  stateOptions: { treatCompleteAsApproved: boolean }
): RegistrationVerificationState {
  return mapBusinessSectionToVerificationState(
    registrationStatus,
    {
      ...businessSection,
      missing_fields: [],
      complete: businessSection.documents_complete,
    },
    stateOptions
  );
}

export function buildStatusVerificationSections(
  progress: RegistrationProgressResponse
): StatusVerificationSection[] {
  const sections: StatusVerificationSection[] = [];
  const { registration_status: registrationStatus } = progress;
  const showSuccessState = shouldShowRegistrationSuccessBanner(progress);
  const stateOptions = { treatCompleteAsApproved: showSuccessState };

  if (progress.sections.business.saved) {
    const businessSection = progress.sections.business;
    const businessState = mapBusinessFieldsVerificationState(
      registrationStatus,
      businessSection,
      stateOptions
    );
    const title =
      businessSection.ownership_type === 'company'
        ? 'Data Badan Usaha'
        : 'Data Bisnis';

    sections.push({
      id: 'business',
      kind: 'business',
      title,
      state: businessState,
      description:
        getBusinessFieldsCorrectionMessage(businessSection) ??
        getBusinessSectionDescription(
          registrationStatus,
          businessSection,
          businessState
        ),
      resumeStep: resolveBusinessSectionStep(progress),
    });

    const documentsState = mapBusinessDocumentsVerificationState(
      registrationStatus,
      businessSection,
      stateOptions
    );

    sections.push({
      id: 'documents',
      kind: 'documents',
      title: 'Dokumen Verifikasi',
      state: documentsState,
      description:
        getBusinessDocumentsCorrectionMessage(businessSection) ??
        getBusinessSectionDescription(
          registrationStatus,
          businessSection,
          documentsState
        ),
      resumeStep: resolveBusinessDocumentsSectionStep(progress),
    });
  }

  progress.sections.outlets.per_outlet.forEach((outletSection, index) => {
    const outletDetails = progress.outlets.find(
      (outlet) => outlet.id === outletSection.outlet_id
    );
    const state = mapOutletSectionToVerificationState(
      registrationStatus,
      outletSection,
      stateOptions
    );
    const title = outletDetails?.name?.trim() || `Outlet ${index + 1}`;

    sections.push({
      id: outletSection.outlet_id,
      kind: 'outlet',
      eyebrow: `Pengajuan ${index + 1}`,
      title,
      state,
      description:
        getOutletCorrectionMessage(outletSection) ??
        getOutletSectionDescription(registrationStatus, outletSection, state),
      resumeStep: resolveOutletSectionStep(
        progress,
        outletSection.outlet_id,
        index
      ),
      outletId: outletSection.outlet_id,
      outletIndex: index,
    });
  });

  return sections;
}
